import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography, Breadcrumbs } from '@mui/material';
import Chart from 'react-apexcharts';
import DashboardCard from '../../shared/DashboardCard';
import { useNavigate } from 'react-router';
import ArrowBack from '@mui/icons-material/ArrowBack';

const EmptyState = () => (
  <Box
    sx={{
      p: { xs: 2, sm: 3 },
      minHeight: { xs: 200, sm: 280 },
      textAlign: 'center',
      border: '1px dashed',
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: 'background.paper',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Typography variant="body1" color="text.secondary"
     sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
    >
      No availability data
    </Typography>
  </Box>
);
  
const CityWideAvailability = ({ cityData = [], countryId, country = 'Canada' }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const navigate = useNavigate();
  const [selectedProvince, setSelectedProvince] = useState(null);

  useEffect(() => {
    setSelectedProvince(null)
  },[countryId, cityData]);

  // Move data processing here
  const categories = selectedProvince 
    ? selectedProvince.townData.map(town => town.townName)
    : cityData?.map(city => city.provinceName) || [];

  const dataValues = selectedProvince
    ? selectedProvince.townData.map(town => Number(town.bedSpaces))
    : cityData?.map(city => Number(city.bedSpaces)) || [];

  // Add this before Chart component
  const chartSeries = [{
    name: 'Available Beds',
    data: dataValues
  }];

  // Move chart options here
  const chartOptions = {
    colors: [primary],
    chart: { 
      type: 'bar', 
      height: '100%',
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
           
          if (!selectedProvince) {
            // Province click
            const clickedProvince = cityData[config.dataPointIndex];
            setSelectedProvince(clickedProvince);
            console.log('Clicked Province ID:', clickedProvince.provinceId);
          } else {
            // City click
            
            const clickedCity = selectedProvince.townData[config.dataPointIndex];
            console.log('Clicked City ID:', clickedCity.townId);
            navigate(`/explore?country=${countryId}&province=${selectedProvince.provinceId}&city=${clickedCity.townId}`);
          }
        }
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '45%',
        distributed: true,
        endingShape: 'rounded',
        cursor: 'pointer' // Add pointer cursor
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: { 
      categories,
      axisBorder: { show: false },
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          fontSize: '12px'
        }
      }
    },
    yaxis: {
      labels: { 
        show: true,
        style: {
          colors: theme.palette.text.secondary,
          fontSize: '12px'
        }
      },
      title: { 
        text: 'Available Beds', 
        style: { 
          fontSize: '12px',
          color: theme.palette.text.primary 
        } 
      },
    },
    tooltip: { 
      theme: theme.palette.mode,
      style: {
        fontSize: '12px'
      },
      y: {
        formatter: (value) => `${value} beds`
      }
    },
    grid: {
      borderColor: theme.palette.divider,
    }
  };

  if (!cityData?.length) {
    return (
      <DashboardCard>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Availability By States</Typography>
        </Box>
        <EmptyState />
      </DashboardCard>
    );
  }

  // Rest of the component code...
  return (
    <DashboardCard sx={{ p: { xs: 0, sm: 3 } }}>
      <Box display="flex"
              flexWrap="wrap"
              gap={1}
              justifyContent="space-between" 
              alignItems="center" 
              mb={{ xs: 2, sm: 3 }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {selectedProvince ? (
            <Breadcrumbs 
              aria-label="availability breadcrumb"
              separator="/"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  sx={{ 
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    color: 'text.primary'
                  }}
                >
                  Availability in
                </Typography>
                <Box 
                  component="span"
                  onClick={() => setSelectedProvince(null)}
                  sx={{ 
                    cursor: 'pointer',
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {country}
                </Box>
              </Box>
              <Typography
                sx={{ 
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  color: 'text.primary'
                }}
              >
                {selectedProvince.provinceName}
              </Typography>
            </Breadcrumbs>
          ) : (
            <Typography variant="h5"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Availability in {country}
            </Typography>
          )}
        </Box>
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={() => navigate(`/explore?country=${countryId}`)}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          View All
        </Button>
      </Box>
      <Box
        sx={{ 
          height: { xs: '250px', sm: '300px', md: '400px' },
          width: '100%',
          '& .apexcharts-canvas': {
            width: '100% !important',
            height: '100% !important'
          },
          '& .apexcharts-canvas .apexcharts-bar': {
            cursor: 'pointer'
          }
        }}
      >
      <Chart options={chartOptions} series={chartSeries} type="bar" width="100%" height="100%" />
      </Box>
    </DashboardCard>
  );
};

export default CityWideAvailability;